import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  enumContainer,
  GetStylesFunction,
  IconHeaderWithSearch,
  LoadingSpinner,
  mixins,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectedFiltersPane,
  LightSelectFilter,
  TableSorting,
  useFormatters,
  useOrganizationsByOrgSecurity,
  UserFilterDto,
  useTableData,
  useTableParams,
  useUserFilters,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {Helmet} from 'react-helmet';

import {HaiYearlyOverviewReportData, HaiYearlyOverviewReportTableAndCharts} from './HaiYearlyOverviewReportTableAndCharts';
import {HaiReportMeasureType} from './HospitalReportLayoutContainer';
import {useAppSelector} from 'src/js/store_hooks';

type HaiYearlyOverviewReportFilters = {
  year: number;
  haiState: string;
  organizationIds: number[] | null;
  measureType: 'HAI_COUNT' | 'HOSP_WITH_HAI_COUNT';
};

type FiltersData = {
  reportOpen: boolean;
  report: HaiYearlyOverviewReportFilters;
  haisByTypeSort: TableSorting;
};

const DEFAULT_YEARLY_OVERVIEW_REPORT_FILTERS_OPEN = true;
const DEFAULT_YEARLY_OVERVIEW_REPORT_FILTERS = () => {
  const currentFullYear = new Date().getFullYear();

  return {
    year: currentFullYear,
    haiState: 'ALL',
    organizationIds: null,
    measureType: 'HAI_COUNT',
  } as HaiYearlyOverviewReportFilters;
};

const DEFAULT_HAIS_BY_TYPE_SORTING: TableSorting = {
  field: 'month',
  direction: 'asc',
};
interface Props {
  config: ConfigObjectType;
}

export const HaiYearlyOverviewReportLayoutContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'yearlyOverviewReport';
  const storageIdPrefix = '/haiYearlyOverviewReport';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const defaultReportFilters = DEFAULT_YEARLY_OVERVIEW_REPORT_FILTERS();
  const filters: FiltersData = useMemo(() => {
    return {
      reportOpen: query?.reportOpen ?? DEFAULT_YEARLY_OVERVIEW_REPORT_FILTERS_OPEN,
      report: {
        ...defaultReportFilters,
        ...(query?.report ?? {}),
      },
      haisByTypeSort: query?.haisByTypeSort ?? DEFAULT_HAIS_BY_TYPE_SORTING,
    };
  }, [query, defaultReportFilters]);

  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({report: newFilters}),
  });

  if (waitForUserFilters) {
    return <LoadingSpinner isVisible marginTop={16} />;
  }

  return (
    <HaiYearlyOverviewReportLayoutContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};

HaiYearlyOverviewReportLayoutContainer.displayName = 'HaiYearlyOverviewReportLayoutContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};
const HaiYearlyOverviewReportLayoutContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const haveDefaultFilters = organizations.length > 0;
  const defaultReportFilters = DEFAULT_YEARLY_OVERVIEW_REPORT_FILTERS();

  const pageTitle = 'navigation.route.haiYearlyOverview';
  const reportUrl = config.service.url.haiYearlyOverviewReport;
  const reportParams = useTableParams({
    year: filters.report.year,
    haiState: filters.report.haiState,
    organizationIds: filters.report.organizationIds,
    measureType: filters.report.measureType,
  });

  const {data: reportData, isLoading} = useTableData<any, HaiYearlyOverviewReportData>(
    reportUrl,
    reportParams,
    'content',
    haveDefaultFilters
  );

  const formatters = useFormatters();
  const {t, tEnum} = formatters;
  const hasFilters = !isEqual(omitNullsy(filters.report), omitNullsy(defaultReportFilters));
  const hasHaisByTypeSorting = !isEqual(omitNullsy(filters.haisByTypeSort), omitNullsy(DEFAULT_HAIS_BY_TYPE_SORTING));
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const haiReportsStateOptions = enumMap.HaiReportsState ?? [];
  const haiMeasureTypeOptions = enumMap.HaiReportMeasureType ?? HaiReportMeasureType;

  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);

  const getYearSince = (year: number) => {
    const currentYear = new Date().getFullYear();
    return Array.from({length: currentYear - year + 1}, (_, i) => ({
      id: (year + i).toString(),
      label: (year + i).toString(),
    })).reverse();
  };

  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);

  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: 'year',
          label: t('filter.hai.year'),
          value: String(filters.report.year),
          defaultValue: defaultReportFilters.year,
        },
        {
          name: 'haiState',
          label: t('reports.filters.haiState'),
          value: tEnum('HaiReportsState', filters.report.haiState),
          defaultValue: defaultReportFilters.haiState,
        },
        {
          name: 'organizationIds',
          label: t('filter.hai.department'),
          value: (filters.report.organizationIds ?? [])
            .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
            .join(', '),
          defaultValue: defaultReportFilters.organizationIds,
        },
        {
          name: 'measureType',
          label: t('reports.filters.haiMeasureType'),
          value: tEnum('HaiReportMeasureType', filters.report.measureType),
          defaultValue: defaultReportFilters.measureType,
        },
      ]}
      tableFilters={filters.report}
      changeTableFilters={(newTableFilters) =>
        changeFilters({
          report: newTableFilters as HaiYearlyOverviewReportFilters,
        })
      }
      formatters={formatters}
    />
  );
  return (
    <div
      css={muiStyles.contentWrapper}
      ref={scrollWrapperRef}
      onScroll={(event) => {
        setDefaultScroll((event.target as HTMLElement).scrollTop);
      }}
    >
      <Helmet title={`${t('reports.list.pageTitle')} | ${t(pageTitle)}`} />
      <div css={muiStyles.splitContentBox}>
        <LightFilterPanel
          open={filters.reportOpen}
          toggleOpen={() =>
            changeFilters({
              reportOpen: !filters.reportOpen,
            })
          }
          hasActiveFilter={hasFilters}
          maxScrollAreaHeight='40vh'
          savedFiltersProps={{
            storageIdPrefix,
            component,
            userFiltersLoading,
            addedUserFilters,
            userFilters,
            changeUserFilter,
            filter: filters.report,
            setFilter: (newFilter) => {
              changeFilters({
                report: newFilter as HaiYearlyOverviewReportFilters,
              });
            },
            config,
          }}
          selectedFilters={selectedFiltersPane}
        >
          <LightSelectFilter
            config={{
              name: 'date',
              ui: {label: 'filter.hai.year'},
            }}
            options={getYearSince(2019)}
            value={filters.report.year}
            onChange={(newValue: string) => {
              changeFilters({
                report: {
                  ...filters.report,
                  year: newValue ? +newValue : defaultReportFilters.year,
                },
              });
            }}
            isClearable={false}
          />
          <LightSelectFilter
            config={{
              name: 'haiState',
              ui: {label: 'reports.filters.haiState'},
            }}
            options={haiReportsStateOptions}
            value={filters.report.haiState}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  haiState: newValue ?? defaultReportFilters.haiState,
                },
              });
            }}
            isClearable={false}
          />
          <LightOrganizationFilter2
            appConfig={config}
            config={{
              name: 'organizationIds',
              ui: {label: 'filter.hai.department'},
            }}
            multi
            isClearable
            value={filters.report.organizationIds?.map((item: number) => item.toString()) ?? null}
            onChange={(newOrganizationIds) => {
              changeFilters({
                report: {
                  ...filters.report,
                  organizationIds: newOrganizationIds?.map((item: string) => Number(item)) ?? null,
                },
              });
            }}
          />
          <LightSelectFilter
            config={{
              name: 'haiMeasureType',
              ui: {label: 'reports.filters.haiMeasureType'},
            }}
            options={sortByMessageKey(haiMeasureTypeOptions ?? []).reverse()}
            value={filters.report.measureType}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  measureType: newValue ?? defaultReportFilters.measureType,
                },
              });
            }}
            isClearable={false}
          />
        </LightFilterPanel>
      </div>
      <HaiYearlyOverviewReportTableAndCharts
        muiStyles={muiStyles}
        storageIdPrefix={storageIdPrefix}
        filters={filters}
        changeFilters={changeFilters}
        hasFilters={hasFilters}
        handleResetFilters={() => changeFilters({report: defaultReportFilters})}
        hasTopSorting={hasHaisByTypeSorting}
        handleResetTopSorting={() =>
          changeFilters({
            haisByTypeSort: DEFAULT_HAIS_BY_TYPE_SORTING,
          })
        }
        formatters={formatters}
        isLoading={isLoading}
        reportData={reportData}
        config={config}
      />
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
    overflow-y: scroll;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    flex: 0 0 auto;
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
    color: var(--dlTheme-700);
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  horizontalSplit: css`
    display: flex;
    align-items: flex-start;
    gap: 32px;
  `,
});
